import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { ChevronDown } from 'lucide-react';

const COUNTRY_CODES = [
    { code: '+91', country: 'IN', label: 'India', maxLen: 10 },
    { code: '+1', country: 'US', label: 'United States', maxLen: 10 },
    { code: '+44', country: 'GB', label: 'United Kingdom', maxLen: 10 },
    { code: '+971', country: 'AE', label: 'UAE', maxLen: 9 },
    { code: '+966', country: 'SA', label: 'Saudi Arabia', maxLen: 9 },
    { code: '+65', country: 'SG', label: 'Singapore', maxLen: 8 },
    { code: '+61', country: 'AU', label: 'Australia', maxLen: 9 },
    { code: '+49', country: 'DE', label: 'Germany', maxLen: 11 },
    { code: '+33', country: 'FR', label: 'France', maxLen: 9 },
    { code: '+81', country: 'JP', label: 'Japan', maxLen: 10 },
    { code: '+86', country: 'CN', label: 'China', maxLen: 11 },
    { code: '+55', country: 'BR', label: 'Brazil', maxLen: 11 },
    { code: '+27', country: 'ZA', label: 'South Africa', maxLen: 9 },
    { code: '+234', country: 'NG', label: 'Nigeria', maxLen: 10 },
    { code: '+254', country: 'KE', label: 'Kenya', maxLen: 9 },
    { code: '+977', country: 'NP', label: 'Nepal', maxLen: 10 },
    { code: '+880', country: 'BD', label: 'Bangladesh', maxLen: 10 },
    { code: '+94', country: 'LK', label: 'Sri Lanka', maxLen: 9 },
    { code: '+60', country: 'MY', label: 'Malaysia', maxLen: 10 },
    { code: '+62', country: 'ID', label: 'Indonesia', maxLen: 12 },
];

const FLAG_EMOJI = (countryCode) => {
    const base = 0x1F1E6 - 65;
    return String.fromCodePoint(
        ...countryCode.split('').map((c) => base + c.charCodeAt(0))
    );
};

/**
 * PhoneInput - A reusable phone number input with country code selector.
 *
 * Props:
 *  - value: string (digits only, no country code)
 *  - onChange: (digits: string) => void
 *  - countryCode: string (e.g. '+91')
 *  - onCountryCodeChange: (code: string) => void
 *  - required, disabled, autoFocus, id, data-testid
 */
const PhoneInput = ({
    value,
    onChange,
    countryCode = '+91',
    onCountryCodeChange,
    required,
    disabled,
    autoFocus,
    id = 'mobile',
    'data-testid': testId = 'input-mobile',
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const dropdownRef = useRef(null);
    const searchRef = useRef(null);

    const selected = COUNTRY_CODES.find((c) => c.code === countryCode) || COUNTRY_CODES[0];

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (open && searchRef.current) {
            searchRef.current.focus();
        }
    }, [open]);

    const filtered = search
        ? COUNTRY_CODES.filter(
            (c) =>
                c.label.toLowerCase().includes(search.toLowerCase()) ||
                c.code.includes(search) ||
                c.country.toLowerCase().includes(search.toLowerCase())
        )
        : COUNTRY_CODES;

    return (
        <div className="flex mt-1" ref={dropdownRef}>
            <div className="relative">
                <button
                    type="button"
                    onClick={() => {
                        if (!disabled) {
                            setOpen(!open);
                            setSearch('');
                        }
                    }}
                    disabled={disabled}
                    className="inline-flex items-center gap-1 px-2.5 h-9 rounded-l-md border border-r-0 border-input bg-stone-50 text-sm text-stone-700 hover:bg-stone-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    data-testid="country-code-selector"
                >
                    <span>{FLAG_EMOJI(selected.country)}</span>
                    <span className="font-medium">{selected.code}</span>
                    <ChevronDown className="h-3 w-3 text-stone-400" />
                </button>
                {open && (
                    <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-stone-200 rounded-md shadow-lg z-50 max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-stone-100">
                            <input
                                ref={searchRef}
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search country..."
                                className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded focus:outline-none focus:ring-1 focus:ring-[hsl(145,60%,30%)]"
                            />
                        </div>
                        <div className="overflow-y-auto max-h-44">
                            {filtered.map((c) => (
                                <button
                                    key={c.code}
                                    type="button"
                                    onClick={() => {
                                        onCountryCodeChange?.(c.code);
                                        setOpen(false);
                                        setSearch('');
                                    }}
                                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-stone-50 transition-colors ${c.code === countryCode ? 'bg-[hsl(145,60%,95%)] text-[hsl(145,60%,20%)] font-medium' : 'text-stone-700'
                                        }`}
                                >
                                    <span>{FLAG_EMOJI(c.country)}</span>
                                    <span className="flex-1">{c.label}</span>
                                    <span className="text-stone-400 text-xs">{c.code}</span>
                                </button>
                            ))}
                            {filtered.length === 0 && (
                                <div className="px-3 py-4 text-sm text-stone-400 text-center">No countries found</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <Input
                id={id}
                type="tel"
                value={value}
                onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}
                required={required}
                disabled={disabled}
                placeholder="9876543210"
                className="rounded-l-none"
                maxLength={selected.maxLen}
                data-testid={testId}
                autoFocus={autoFocus}
            />
        </div>
    );
};

export { PhoneInput, COUNTRY_CODES };
export default PhoneInput;
